import {
  ConfirmationDialog,
  TextField,
  Typography,
} from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import Proptypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import {
  selectedMatchIdAtom,
  selectedSportAtom,
} from '../../atoms/selectedMetadata';
import { matchIdCompliant } from '../../utils/string';
import themeHelper from '../../utils/themeHelper';
import InputCompetition from '../InputCompetition';
import SelectTeam from '../SelectTeam';
import { FOOTBALL_IDENTIFIER, NFL_IDENTIFIER } from '../Sport/Sport';
import { MatchSelection } from './MatchSelection';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeHelper.spacing()};
`;

export function SelectMatchId({ id, size }) {
  const intl = useIntl();
  const dialogId = `dialog-${id}`;

  const sport = useAtomValue(selectedSportAtom);
  const [matchId, setMatchId] = useAtom(selectedMatchIdAtom);

  const [homeTeamId, setHomeTeamId] = useState();
  const [awayTeamId, setAwayTeamId] = useState();
  const [seasonId, setSeasonId] = useState(null);
  const [competitionId, setCompetitionId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const closeAndReset = () => {
    setIsOpen(false);
    setSeasonId(null);
    setCompetitionId(null);
    setAwayTeamId('');
    setHomeTeamId('');
  };

  const updateMatchId = (newMatchId) => {
    setMatchId(newMatchId);
    closeAndReset();
  };

  const handleChange = (event) => {
    const { value } = event.currentTarget;
    updateMatchId(matchIdCompliant(value));
  };

  const SUPPORTED_SPORTS = [FOOTBALL_IDENTIFIER, NFL_IDENTIFIER];
  const disableLookup = !SUPPORTED_SPORTS.includes(sport);

  const handleOpen = () => {
    if (!disableLookup) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    closeAndReset();
  };

  const handleCompetitionChange = (option) => {
    if (option) {
      setSeasonId(option.value.seasonId);
      setCompetitionId(option.value.competitionId);
    }
  };

  const handleHomeTeamIdChange = (selectedOption) => {
    setHomeTeamId(selectedOption);
  };

  const handleAwayTeamIdChange = (selectedOption) => {
    setAwayTeamId(selectedOption);
  };

  const lookupMessage = intl.formatMessage({
    defaultMessage: "Lookup a Match's ID",
    id: '91d91',
  });
  const lookupUnavailable = intl.formatMessage({
    defaultMessage: 'Lookup unavailable for the selected sport',
    id: '3f9ce',
  });

  const lookupTooltip = disableLookup ? lookupUnavailable : lookupMessage;
  const displayTeams = !!(competitionId && seasonId);

  return (
    <>
      <TextField
        adornmentIcon="Search"
        adornmentOnClick={handleOpen}
        adornmentTitle={lookupTooltip}
        id={id}
        label={intl.formatMessage({
          defaultMessage: 'Match ID',
          id: '7b699',
        })}
        onChange={handleChange}
        placeholder={intl.formatMessage({
          defaultMessage: 'Match ID',
          id: '7b699',
        })}
        size={size}
        type="text"
        value={matchId}
        variant="secondary"
      />
      <ConfirmationDialog
        cancelLabel={null}
        confirmLabel={intl.formatMessage({
          defaultMessage: 'Cancel',
          id: 'ea478',
        })}
        id={dialogId}
        isOpen={isOpen}
        onConfirm={handleClose}
        title={intl.formatMessage({
          defaultMessage: 'Lookup Match ID',
          id: 'a1b27',
        })}
      >
        {isOpen && (
          <Container>
            <Typography variant="bodyRegular">
              <FormattedMessage
                defaultMessage="Search for match id based on teams and season."
                id="6b954"
              />
            </Typography>
            <InputCompetition
              id={`${id}-input-competition`}
              competitionId={competitionId}
              onChange={handleCompetitionChange}
              seasonId={seasonId}
              sport={sport}
            />
            <SelectTeam
              competitionId={competitionId}
              disabled={displayTeams}
              fullWidth
              id={`${id}-home-team-input`}
              label={intl.formatMessage({
                defaultMessage: 'Home Team',
                id: '1fdd2',
              })}
              onChange={handleHomeTeamIdChange}
              placeholder={intl.formatMessage({
                defaultMessage: 'Select Home Team',
                id: '2fc9a',
              })}
              selectedTeamId={homeTeamId}
              seasonId={seasonId}
              sport={sport}
            />
            <SelectTeam
              competitionId={competitionId}
              disabled={displayTeams}
              fullWidth
              id={`${id}-away-team-input`}
              label={intl.formatMessage({
                defaultMessage: 'Away Team',
                id: 'f6add',
              })}
              onChange={handleAwayTeamIdChange}
              placeholder={intl.formatMessage({
                defaultMessage: 'Select Away Team',
                id: 'd94cf',
              })}
              selectedTeamId={awayTeamId}
              seasonId={seasonId}
              sport={sport}
            />
            <MatchSelection
              awayTeamId={+awayTeamId}
              competitionId={competitionId}
              homeTeamId={+homeTeamId}
              onMatchSelection={updateMatchId}
              seasonId={seasonId}
              sport={sport}
            />
          </Container>
        )}
      </ConfirmationDialog>
    </>
  );
}

SelectMatchId.propTypes = {
  id: Proptypes.string.isRequired,
  size: Proptypes.string,
};
SelectMatchId.defaultProps = {
  size: undefined,
};
export default { SelectMatchId };
